import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useSearchParams, useNavigate } from 'react-router';
import { navigationStateFrom } from '@/types/navigation';
import {
    SearchLg,
    Eye,
    Upload01,
    Package,
    ArrowLeft,
    X,
    Table as TableIcon,
    LayoutGrid01,
    Globe02
} from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Select } from '@/components/base/select/select';
import { Badge } from '@/components/base/badges/badges';
import { Table, TableCard } from '@/components/application/table/table';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { DistributionChart } from '@/components/application/charts/distribution-chart';
import { containerService } from '@/data/mock-containers';
import { accountService } from '@/data/mock-accounts';
import type { Container, ContainerStatus, RiskLevel, DocumentStatus, Commodity } from '@/types/container';
import { useTranslation } from 'react-i18next';
import { storage } from '@/lib/local-storage';
import { useI18n } from '@/hooks/use-i18n';
import {
    translateCommodity,
    extractCountryFromRoute,
    mapToSpecificPort
} from '@/utils/data-extractors';
import { ContainerListView } from '@/components/application/containers/container-list-view';
import { Button } from '@/components/base/buttons/button';
import { useInfiniteScroll } from '@/components/application/containers/use-infinite-scroll';
import WorldRoutesMapPreview from '@/components/application/map/world-routes-map-preview';

type ViewMode = 'recap' | 'table' | 'list';

export const ReceiverContainersPage = () => {
    const { t } = useTranslation(['pages:containers', 'common']);
    const { translateContainerStatus, translateDocumentStatus, translateRiskLevel } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [containers, setContainers] = useState<Container[]>([]);
    const [shippers, setShippers] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
    const [riskLevelFilter, setRiskLevelFilter] = useState<RiskLevel | ''>('');
    const [documentStatusFilter, setDocumentStatusFilter] = useState<DocumentStatus | ''>('');
    const [shipperFilter, setShipperFilter] = useState('');
    const [commodityFilter, setCommodityFilter] = useState<Commodity | ''>('');
    const [originFilter, setOriginFilter] = useState('');
    const [arrivalPortFilter, setArrivalPortFilter] = useState('');
    const getValidViewMode = (view: string | null): ViewMode => {
        const validModes: ViewMode[] = ['recap', 'table', 'list'];
        return (validModes.includes(view as ViewMode) ? view : 'recap') as ViewMode;
    };
    const [viewMode, setViewMode] = useState<ViewMode>(
        getValidViewMode(searchParams.get('view'))
    );
    const [displayedCount, setDisplayedCount] = useState(20);

    // Aggiorna query parameter quando viewMode cambia
    useEffect(() => {
        const currentView = searchParams.get('view');
        if (viewMode !== currentView) {
            const newParams = new URLSearchParams(searchParams);
            if (viewMode === 'recap') {
                newParams.delete('view');
            } else {
                newParams.set('view', viewMode);
            }
            setSearchParams(newParams, { replace: true });
        }
    }, [viewMode, searchParams, setSearchParams]);

    // Reset displayedCount quando cambia viewMode o filtri
    useEffect(() => {
        setDisplayedCount(20);
    }, [viewMode, searchTerm, statusFilter, riskLevelFilter, documentStatusFilter, shipperFilter, commodityFilter, originFilter, arrivalPortFilter]);

    const statusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'In Transit', label: t('containerStatus.inTransit', { ns: 'types' }) },
        { id: 'Delivered', label: t('containerStatus.delivered', { ns: 'types' }) },
        { id: 'Delayed', label: t('containerStatus.delayed', { ns: 'types' }) },
        { id: 'At Port', label: t('containerStatus.atPort', { ns: 'types' }) }
    ];

    const riskLevelOptions = [
        { id: '', label: t('list.filters.allLevels') },
        { id: 'Low', label: t('riskLevel.low', { ns: 'types' }) },
        { id: 'Medium', label: t('riskLevel.medium', { ns: 'types' }) },
        { id: 'High', label: t('riskLevel.high', { ns: 'types' }) },
        { id: 'Critical', label: t('riskLevel.critical', { ns: 'types' }) }
    ];

    const documentStatusOptions = [
        { id: '', label: t('list.filters.allStatuses') },
        { id: 'Completo', label: t('documentStatus.complete', { ns: 'types' }) },
        { id: 'Pending', label: t('documentStatus.pending', { ns: 'types' }) },
        { id: 'Error', label: t('documentStatus.error', { ns: 'types' }) }
    ];

    // Generate commodity options from containers
    const commodityOptions = useMemo(() => {
        const commodities = [...new Set(containers.map(c => c.commodity))];
        return [
            { id: '', label: t('list.filters.allCommodities') },
            ...commodities.map(commodity => ({
                id: commodity,
                label: translateCommodity(commodity, 'it')
            }))
        ];
    }, [containers, t]);

    // Generate origin options from containers
    const originOptions = useMemo(() => {
        const origins = [...new Set(containers.map(c => extractCountryFromRoute(c.route.from)))];
        return [
            { id: '', label: t('list.filters.allOrigins') },
            ...origins.map(origin => ({ id: origin, label: origin }))
        ];
    }, [containers, t]);

    // Generate arrival port options from containers
    const arrivalPortOptions = useMemo(() => {
        const ports = [...new Set(containers.map(c => mapToSpecificPort(c.route.to)))];
        return [
            { id: '', label: t('list.filters.allPorts') },
            ...ports.map(port => ({ id: port, label: port }))
        ];
    }, [containers, t]);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            // Get current user ID from localStorage
            const accountId = storage.getItem('accountId') || '2';

            // Load containers for this receiver
            const allContainers = containerService.getAll();
            const receiverContainers = allContainers.filter(c => c.receiverId === accountId);
            setContainers(receiverContainers);

            // Load shippers for filter
            const allAccounts = accountService.getAll();
            const shipperAccounts = allAccounts.filter(acc => acc.userType === 'SHIPPER');
            setShippers(shipperAccounts);

            setIsLoading(false);
        };
        loadData();
    }, []);

    // Initialize filters from URL parameters
    useEffect(() => {
        const commodity = searchParams.get('commodity') as Commodity | null;
        const origin = searchParams.get('origin');
        const arrivalPort = searchParams.get('arrivalPort');
        const status = searchParams.get('status') as ContainerStatus | null;
        const riskLevel = searchParams.get('riskLevel') as RiskLevel | null;
        const documentStatus = searchParams.get('documentStatus') as DocumentStatus | null;
        const shipper = searchParams.get('shipper');
        const search = searchParams.get('search');

        if (commodity) setCommodityFilter(commodity);
        if (origin) setOriginFilter(origin);
        if (arrivalPort) setArrivalPortFilter(arrivalPort);
        if (status) setStatusFilter(status);
        if (riskLevel) setRiskLevelFilter(riskLevel);
        if (documentStatus) setDocumentStatusFilter(documentStatus);
        if (shipper) setShipperFilter(shipper);
        if (search) setSearchTerm(search);
    }, [searchParams]);

    // Update URL when filters change
    const updateURL = (newParams: Record<string, string>) => {
        const params = new URLSearchParams(searchParams);

        Object.entries(newParams).forEach(([key, value]) => {
            if (value) {
                params.set(key, value);
            } else {
                params.delete(key);
            }
        });

        setSearchParams(params);
    };

    const getRiskLevelColor = (riskLevel: string) => {
        switch (riskLevel) {
            case 'Low': return 'success';
            case 'Medium': return 'warning';
            case 'High': return 'error';
            case 'Critical': return 'error';
            default: return 'gray';
        }
    };

    const getDocumentStatusColor = (status: string) => {
        switch (status) {
            case 'Completo': return 'success';
            case 'Pending': return 'warning';
            case 'Error': return 'error';
            default: return 'gray';
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'In Transit': return 'brand';
            case 'Delivered': return 'success';
            case 'Delayed': return 'error';
            case 'At Port': return 'warning';
            default: return 'gray';
        }
    };

    const getShipperName = (shipperId: string) => {
        const shipper = shippers.find(s => s.id === shipperId);
        return shipper ? shipper.legalName : shipperId;
    };

    // Helper per convertire color names a hex
    const getColorHex = (colorName: string): string => {
        switch (colorName) {
            case 'brand': return '#6938ef'; // brand-600
            case 'success': return '#079485'; // success-600
            case 'warning': return '#dc6803'; // warning-600
            case 'error': return '#d92d20'; // error-600
            default: return '#6b7280'; // gray-500
        }
    };

    // Filtra i container in base ai filtri selezionati
    const filteredContainers = useMemo(() => {
        return containers.filter(container => {
            const matchesSearch = !searchTerm ||
                container.containerNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                container.route.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
                container.route.to.toLowerCase().includes(searchTerm.toLowerCase()) ||
                getShipperName(container.shipperId).toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus = !statusFilter || container.status === statusFilter;
            const matchesRiskLevel = !riskLevelFilter || container.riskLevel === riskLevelFilter;
            const matchesDocumentStatus = !documentStatusFilter || container.documentStatus === documentStatusFilter;
            const matchesShipper = !shipperFilter || container.shipperId === shipperFilter;
            const matchesCommodity = !commodityFilter || container.commodity === commodityFilter;
            const matchesOrigin = !originFilter || extractCountryFromRoute(container.route.from) === originFilter;
            const matchesArrivalPort = !arrivalPortFilter || mapToSpecificPort(container.route.to) === arrivalPortFilter;

            return matchesSearch && matchesStatus && matchesRiskLevel && matchesDocumentStatus &&
                matchesShipper && matchesCommodity && matchesOrigin && matchesArrivalPort;
        });
    }, [containers, searchTerm, statusFilter, riskLevelFilter, documentStatusFilter, shipperFilter, commodityFilter, originFilter, arrivalPortFilter, shippers]);

    // Container da mostrare (con lazy loading per list/table/spreadsheet)
    const displayedContainers = useMemo(() => {
        if (viewMode === 'recap') {
            // In recap mostra solo gli ultimi 10
            return filteredContainers.slice(0, 10);
        }
        // Per altre viste, mostra fino a displayedCount
        return filteredContainers.slice(0, displayedCount);
    }, [filteredContainers, displayedCount, viewMode]);

    const hasMore = displayedCount < filteredContainers.length;

    const { loadMoreRef } = useInfiniteScroll({
        hasMore,
        loadMore: () => {
            setDisplayedCount(prev => Math.min(prev + 20, filteredContainers.length));
        },
    });

    // Calcola distribuzioni per i grafici basati sui container filtrati
    const distributionData = useMemo(() => {
        const total = filteredContainers.length;

        // Risk Levels
        const riskDistribution = filteredContainers.reduce((acc, c) => {
            acc[c.riskLevel] = (acc[c.riskLevel] || 0) + 1;
            return acc;
        }, {} as Record<RiskLevel, number>);

        const riskData = [
            { label: translateRiskLevel('Low'), value: riskDistribution.Low || 0, color: getColorHex('success') },
            { label: translateRiskLevel('Medium'), value: riskDistribution.Medium || 0, color: getColorHex('warning') },
            { label: translateRiskLevel('High'), value: riskDistribution.High || 0, color: getColorHex('error') },
            { label: translateRiskLevel('Critical'), value: riskDistribution.Critical || 0, color: getColorHex('error') },
        ];

        // Container Status
        const statusDistribution = filteredContainers.reduce((acc, c) => {
            acc[c.status] = (acc[c.status] || 0) + 1;
            return acc;
        }, {} as Record<ContainerStatus, number>);

        const statusData = [
            { label: translateContainerStatus('In Transit'), value: statusDistribution['In Transit'] || 0, color: getColorHex('brand') },
            { label: translateContainerStatus('Delivered'), value: statusDistribution.Delivered || 0, color: getColorHex('success') },
            { label: translateContainerStatus('Delayed'), value: statusDistribution.Delayed || 0, color: getColorHex('error') },
            { label: translateContainerStatus('At Port'), value: statusDistribution['At Port'] || 0, color: getColorHex('warning') },
        ];

        // Document Status
        const documentDistribution = filteredContainers.reduce((acc, c) => {
            acc[c.documentStatus] = (acc[c.documentStatus] || 0) + 1;
            return acc;
        }, {} as Record<DocumentStatus, number>);

        const documentData = [
            { label: translateDocumentStatus('Completo'), value: documentDistribution.Completo || 0, color: getColorHex('success') },
            { label: translateDocumentStatus('Pending'), value: documentDistribution.Pending || 0, color: getColorHex('warning') },
            { label: translateDocumentStatus('Error'), value: documentDistribution.Error || 0, color: getColorHex('error') },
        ];

        return { riskData, statusData, documentData, total };
    }, [filteredContainers, translateRiskLevel, translateContainerStatus, translateDocumentStatus]);

    // Helper functions for filter changes
    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        updateURL({ search: value });
    };

    const handleStatusChange = (value: ContainerStatus | '') => {
        setStatusFilter(value);
        updateURL({ status: value });
    };

    const handleRiskLevelChange = (value: RiskLevel | '') => {
        setRiskLevelFilter(value);
        updateURL({ riskLevel: value });
    };

    const handleDocumentStatusChange = (value: DocumentStatus | '') => {
        setDocumentStatusFilter(value);
        updateURL({ documentStatus: value });
    };

    const handleShipperChange = (value: string) => {
        setShipperFilter(value);
        updateURL({ shipper: value });
    };

    const handleCommodityChange = (value: Commodity | '') => {
        setCommodityFilter(value);
        updateURL({ commodity: value });
    };

    const handleOriginChange = (value: string) => {
        setOriginFilter(value);
        updateURL({ origin: value });
    };

    const handleArrivalPortChange = (value: string) => {
        setArrivalPortFilter(value);
        updateURL({ arrivalPort: value });
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setRiskLevelFilter('');
        setDocumentStatusFilter('');
        setShipperFilter('');
        setCommodityFilter('');
        setOriginFilter('');
        setArrivalPortFilter('');
        setSearchParams(new URLSearchParams());
    };

    const hasActiveFilters = !!(searchTerm || statusFilter || riskLevelFilter || documentStatusFilter ||
        shipperFilter || commodityFilter || originFilter || arrivalPortFilter);

    const getActiveBreadcrumb = () => {
        if (commodityFilter) return `Commodity: ${translateCommodity(commodityFilter, 'it')}`;
        if (originFilter) return `Origine: ${originFilter}`;
        if (arrivalPortFilter) return `Porto: ${arrivalPortFilter}`;
        if (statusFilter) return `Status: ${translateContainerStatus(statusFilter)}`;
        if (riskLevelFilter) return `Rischio: ${translateRiskLevel(riskLevelFilter)}`;
        if (documentStatusFilter) return `Documenti: ${translateDocumentStatus(documentStatusFilter)}`;
        return null;
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <p className="text-lg text-tertiary">{t('list.loading')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Breadcrumb and Header */}
            <div className="space-y-4">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-sm text-tertiary">
                    <Link to="/receiver/dashboard" className="hover:text-primary">
                        Dashboard
                    </Link>
                    <span>›</span>
                    <span className="text-primary">Container</span>
                    {getActiveBreadcrumb() && (
                        <>
                            <span>›</span>
                            <span className="text-primary">{getActiveBreadcrumb()}</span>
                        </>
                    )}
                </div>

                {/* Header with actions */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('receiver.title')}</h1>
                        <p className="text-tertiary mt-1">
                            {t('receiver.subtitle')} ({filteredContainers.length} di {containers.length})
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 rounded-lg bg-secondary p-1 ring-1 ring-secondary_alt">
                            <Button
                                size="sm"
                                color={viewMode === 'recap' ? 'primary' : 'secondary'}
                                onClick={() => setViewMode('recap')}
                                iconLeading={LayoutGrid01}
                            >
                                Recap
                            </Button>
                            <Button
                                size="sm"
                                color={viewMode === 'list' ? 'primary' : 'secondary'}
                                onClick={() => setViewMode('list')}
                                iconLeading={Package}
                            >
                                Lista
                            </Button>
                            <Button
                                size="sm"
                                color={viewMode === 'table' ? 'primary' : 'secondary'}
                                onClick={() => setViewMode('table')}
                                iconLeading={TableIcon}
                            >
                                Tabella
                            </Button>
                            <Button
                                size="sm"
                                color="secondary"
                                onClick={() => navigate('/receiver/containers/world-map', { state: navigationStateFrom(location) })}
                                iconLeading={Globe02}
                            >
                                {t('list.viewMap')}
                            </Button>
                        </div>
                        {hasActiveFilters && (
                            <Button
                                color="secondary"
                                size="md"
                                onClick={clearAllFilters}
                                iconLeading={X}
                            >
                                {t('list.clearFilters')}
                            </Button>
                        )}
                        <Button
                            color="secondary"
                            size="md"
                            onClick={() => navigate('/receiver/dashboard')}
                            iconLeading={ArrowLeft}
                        >
                            {t('list.backToDashboard')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* World Routes Map Preview e Distribution Charts - solo in modalità Recap */}
            {viewMode === 'recap' && (
                <>
                    {/* World Routes Map Preview */}
                    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <h3 className="text-lg font-semibold text-primary">{t('worldMap.title')}</h3>
                                <p className="text-sm text-tertiary">{t('worldMap.updateInfo')}</p>
                            </div>
                            <Link
                                to="/receiver/containers/world-map"
                                state={navigationStateFrom(location)}
                                className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-secondary shadow-xs-skeumorphic ring-1 ring-primary ring-inset hover:bg-primary_hover hover:text-secondary_hover"
                            >
                                <Eye className="size-4" />
                                {t('list.viewMap')}
                            </Link>
                        </div>
                        <WorldRoutesMapPreview height={360} role="receiver" />
                    </div>

                    {/* Distribution Charts */}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                        <DistributionChart
                            title={t('list.charts.riskLevels', { ns: 'pages:containers' }) || 'Risk Levels Distribution'}
                            data={distributionData.riskData}
                            total={distributionData.total}
                        />
                        <DistributionChart
                            title={t('list.charts.containerStatus', { ns: 'pages:containers' }) || 'Container Status Distribution'}
                            data={distributionData.statusData}
                            total={distributionData.total}
                        />
                        <DistributionChart
                            title={t('list.charts.documentStatus', { ns: 'pages:containers' }) || 'Document Status Distribution'}
                            data={distributionData.documentData}
                            total={distributionData.total}
                        />
                    </div>
                </>
            )}

            {/* Filters */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
                    <Input
                        placeholder={t('receiver.searchPlaceholder')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                        icon={SearchLg}
                        size="md"
                    />

                    <Select
                        placeholder={t('list.filters.statusPlaceholder')}
                        items={statusOptions}
                        selectedKey={statusFilter}
                        onSelectionChange={(key) => handleStatusChange(key as ContainerStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.riskPlaceholder')}
                        items={riskLevelOptions}
                        selectedKey={riskLevelFilter}
                        onSelectionChange={(key) => handleRiskLevelChange(key as RiskLevel | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.documentsPlaceholder')}
                        items={documentStatusOptions}
                        selectedKey={documentStatusFilter}
                        onSelectionChange={(key) => handleDocumentStatusChange(key as DocumentStatus | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('receiver.shipperPlaceholder')}
                        items={[
                            { id: '', label: t('receiver.allShippers') },
                            ...shippers.map(shipper => ({ id: shipper.id, label: shipper.legalName }))
                        ]}
                        selectedKey={shipperFilter}
                        onSelectionChange={(key) => handleShipperChange(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.commodityPlaceholder')}
                        items={commodityOptions}
                        selectedKey={commodityFilter}
                        onSelectionChange={(key) => handleCommodityChange(key as Commodity | '')}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.originPlaceholder')}
                        items={originOptions}
                        selectedKey={originFilter}
                        onSelectionChange={(key) => handleOriginChange(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>

                    <Select
                        placeholder={t('list.filters.portPlaceholder')}
                        items={arrivalPortOptions}
                        selectedKey={arrivalPortFilter}
                        onSelectionChange={(key) => handleArrivalPortChange(key as string)}
                        size="md"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>
            </div>

            {/* Containers View */}
            <div key={viewMode}>
                {viewMode === 'recap' ? (
                    <div className="space-y-6">
                        {/* Ultimi 10 container */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-primary">
                                    Ultimi 10 Container ({displayedContainers.length})
                                </h3>
                                <Link
                                    to="/receiver/containers"
                                    className="text-sm text-brand-secondary hover:text-brand-secondary_hover"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setViewMode('list');
                                    }}
                                >
                                    Vedi tutti →
                                </Link>
                            </div>
                            <TableCard.Root>
                                <Table>
                                    <Table.Header>
                                        <Table.Head label={t('receiver.headers.containerId')} isRowHeader />
                                        <Table.Head label={t('receiver.headers.shipper')} />
                                        <Table.Head label={t('receiver.headers.route')} />
                                        <Table.Head label={t('receiver.headers.progress')} />
                                        <Table.Head label={t('receiver.headers.eta')} />
                                        <Table.Head label={t('receiver.headers.status')} />
                                        <Table.Head label={t('receiver.headers.actions')} />
                                    </Table.Header>
                                    <Table.Body>
                                        {displayedContainers.map((container) => (
                                            <Table.Row key={container.id}>
                                                <Table.Cell>
                                                    <div className="font-medium text-primary">{container.containerNumber}</div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="text-sm text-tertiary">
                                                        {getShipperName(container.shipperId)}
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="text-sm text-tertiary">
                                                        {container.route.from} → {container.route.to}
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="flex items-center gap-2">
                                                        <ProgressBar
                                                            value={container.progress}
                                                            min={0}
                                                            max={100}
                                                        />
                                                        <span className="text-sm text-tertiary">{container.progress}%</span>
                                                    </div>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <span className="text-sm text-tertiary">
                                                        {container.eta.toLocaleDateString('it-IT')}
                                                    </span>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={getStatusColor(container.status)} size="sm">
                                                        {translateContainerStatus(container.status)}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <div className="flex items-center gap-2">
                                                        <Link
                                                            to={(() => {
                                                                const currentView = searchParams.get('view');
                                                                return currentView
                                                                    ? `/receiver/containers/${container.id}?view=${currentView}`
                                                                    : `/receiver/containers/${container.id}`;
                                                            })()}
                                                            className="text-brand-secondary hover:text-brand-secondary_hover"
                                                        >
                                                            <Eye className="size-4" />
                                                        </Link>
                                                    </div>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table>
                            </TableCard.Root>
                        </div>
                    </div>
                ) : viewMode === 'list' ? (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-primary">
                                {t('receiver.title')} ({filteredContainers.length})
                            </h3>
                        </div>
                        <ContainerListView
                            containers={displayedContainers}
                            onContainerSelect={(id) => {
                                // Navigate to container detail preserving view
                                const currentView = searchParams.get('view');
                                const url = currentView
                                    ? `/receiver/containers/${id}?view=${currentView}`
                                    : `/receiver/containers/${id}`;
                                navigate(url, { state: navigationStateFrom(location) });
                            }}
                        />
                        {hasMore && (
                            <div ref={loadMoreRef} className="flex justify-center py-4">
                                <div className="text-sm text-tertiary">Caricamento...</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <TableCard.Root>
                            <TableCard.Header
                                title={`${t('receiver.title')} (${filteredContainers.length})`}
                                description={t('receiver.description')}
                            />

                            <Table>
                                <Table.Header>
                                    <Table.Head label={t('receiver.headers.containerId')} isRowHeader />
                                    <Table.Head label={t('receiver.headers.shipper')} />
                                    <Table.Head label={t('receiver.headers.route')} />
                                    <Table.Head label={t('receiver.headers.shippingCompany')} />
                                    <Table.Head label={t('receiver.headers.progress')} />
                                    <Table.Head label={t('receiver.headers.eta')} />
                                    <Table.Head label={t('receiver.headers.status')} />
                                    <Table.Head label={t('receiver.headers.docStatus')} />
                                    <Table.Head label={t('receiver.headers.riskLevel')} />
                                    <Table.Head label={t('receiver.headers.value')} />
                                    <Table.Head label={t('receiver.headers.actions')} />
                                </Table.Header>
                                <Table.Body>
                                    {displayedContainers.map((container) => (
                                        <Table.Row key={container.id}>
                                            <Table.Cell>
                                                <div className="font-medium text-primary">{container.containerNumber}</div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">
                                                    {getShipperName(container.shipperId)}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="text-sm text-tertiary">
                                                    {container.route.from} → {container.route.to}
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {container.shippingCompany ?? '—'}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <ProgressBar
                                                        value={container.progress}
                                                        min={0}
                                                        max={100}
                                                    />
                                                    <span className="text-sm text-tertiary">{container.progress}%</span>
                                                </div>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {container.eta.toLocaleDateString('it-IT')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getStatusColor(container.status)} size="sm">
                                                    {translateContainerStatus(container.status)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getDocumentStatusColor(container.documentStatus)} size="sm">
                                                    {translateDocumentStatus(container.documentStatus)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <Badge color={getRiskLevelColor(container.riskLevel)} size="sm">
                                                    {translateRiskLevel(container.riskLevel)}
                                                </Badge>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <span className="text-sm text-tertiary">
                                                    {container.value.currency} {container.value.amount.toLocaleString()}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={(() => {
                                                            const currentView = searchParams.get('view');
                                                            return currentView
                                                                ? `/receiver/containers/${container.id}?view=${currentView}`
                                                                : `/receiver/containers/${container.id}`;
                                                        })()}
                                                        className="text-brand-secondary hover:text-brand-secondary_hover"
                                                    >
                                                        <Eye className="size-4" />
                                                    </Link>
                                                    <button className="text-tertiary hover:text-primary">
                                                        <Upload01 className="size-4" />
                                                    </button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </TableCard.Root>
                        {hasMore && (
                            <div ref={loadMoreRef} className="flex justify-center py-4">
                                <div className="text-sm text-tertiary">Caricamento...</div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {filteredContainers.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-tertiary mb-4">
                        <Package className="size-12 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-primary mb-2">{t('receiver.noContainers.title')}</h3>
                        <p className="text-tertiary">
                            {searchTerm || statusFilter || riskLevelFilter || documentStatusFilter || shipperFilter
                                ? t('receiver.noContainers.message')
                                : t('receiver.noContainers.emptyMessage')
                            }
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};
